import {
  FIELD_LOG_FOOTAGE_FILTER_STATUSES,
  FIELD_LOG_FOOTAGE_STATUSES,
  FIELD_LOG_REVIEW_ERROR_MESSAGES,
} from '@/features/field-log/config/today';
import type { FieldLogFootageStatus } from '@/features/field-log/types/fieldLog';
import { ApiHttpError } from '@/shared/api/client';
import { i18n } from '@lingui/core';
import { z } from 'zod';

export const fieldLogFootageStatusSchema = z.enum(FIELD_LOG_FOOTAGE_STATUSES);

const fieldLogFootageFilterStatusSet: ReadonlySet<FieldLogFootageStatus> = new Set(
  FIELD_LOG_FOOTAGE_FILTER_STATUSES,
);

/** Keeps URL-owned filters aligned with the statuses offered by the filter control. */
export const fieldLogFootageStatusFilterSchema = z
  .array(fieldLogFootageStatusSchema)
  .transform((statuses) => {
    const filtered = statuses.filter((status) => fieldLogFootageFilterStatusSet.has(status));
    return filtered.length > 0 ? filtered : undefined;
  })
  .optional()
  .catch(undefined);

const fieldLogReviewErrorSchema = z
  .object({
    error: z.string(),
    reason: z.string().nullable(),
    sessionId: z.string().nullable(),
    currentReviewVersion: z.number().int().nonnegative().nullable(),
  })
  .strict();

/** Reads the documented error body; error payloads are an untyped transport boundary. */
export function fieldLogReviewErrorKey(error: unknown): string | undefined {
  if (!(error instanceof ApiHttpError)) return undefined;
  const parsed = fieldLogReviewErrorSchema.safeParse(error.payload);
  if (!parsed.success) return error.serverMessage || undefined;
  return parsed.data.reason ?? parsed.data.error;
}

export function fieldLogReviewErrorMessage(error: unknown, fallback: string): string {
  const key = fieldLogReviewErrorKey(error);
  const descriptor = key ? FIELD_LOG_REVIEW_ERROR_MESSAGES[key] : undefined;
  return descriptor ? i18n._(descriptor) : fallback;
}
